import { useCallback, useLayoutEffect, type KeyboardEvent } from "react";
import { usePickerChain, usePickerLevel } from "./PickerChainContext";
import { usePickerQuery, usePickerRoot } from "./PickerContext";
import { firstPressableRow } from "./firstPressableRow";
import { isSearchCharacter } from "./isSearchCharacter";
import { preventBaseUIHandler } from "./usePickerLevelKeyboard";
import { useLatestCallback } from "./usePickerStable";

/**
 * Under `cellEditor`, a top-level `Picker.Search` starts from the key that
 * opened the grid editor. The grid has already consumed that keystroke, and
 * the search only takes focus a frame after the popup opens, so the letter
 * would otherwise be lost. Seeding on mount runs after a multi-select's
 * open reset, and as typing so `onInputValueChange` consumers see it.
 */
export const usePickerCellEditorSeed = () => {
    const root = usePickerRoot();
    const chain = usePickerChain();
    const level = usePickerLevel();
    const openingKey = chain?.cellEditor?.openingKey;
    const isTopLevel = (level?.depth ?? 0) === 0;
    const seedQuery = useLatestCallback(root.setInputValue);

    useLayoutEffect(() => {
        if (isTopLevel && openingKey != null && isSearchCharacter(openingKey)) {
            seedQuery?.(openingKey);
        }
    }, [isTopLevel, openingKey, seedQuery]);
};

/**
 * Under `cellEditor`, once a search is typed, Enter with no highlighted row
 * presses the first enabled row (a seeded query has no highlight until the
 * next keystroke), and Tab presses the highlighted row or that first row
 * unless it is already picked, so Tab never toggles off the row it lands on.
 * With nothing typed Tab only leaves the cell, so a row the pointer happens
 * to highlight is never written. Action rows (create, clear) only answer
 * Enter: Tab must never create or clear. Tab is never prevented or stopped,
 * so the grid still moves to the next cell and a multi-select cell commits
 * its picks.
 */
export const usePickerCellEditorKeyDown = () => {
    const chain = usePickerChain();
    const level = usePickerLevel();
    const { searchText } = usePickerQuery();
    const isCellEditor = chain?.cellEditor !== undefined;

    return useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            const isTab = event.key === "Tab";
            if (
                !isCellEditor ||
                event.defaultPrevented ||
                (!isTab && event.key !== "Enter")
            ) {
                return;
            }
            const isSearching = searchText.trim() !== "";
            const popup = level?.popupRef.current;
            const highlighted = popup?.querySelector("[data-highlighted]");
            const highlightedRow =
                highlighted instanceof HTMLElement ? highlighted : null;
            if (isTab) {
                const row = highlightedRow ?? firstPressableRow(popup);
                if (
                    isSearching &&
                    row !== null &&
                    !row.hasAttribute("data-action-item") &&
                    row.getAttribute("aria-selected") !== "true"
                ) {
                    row.click();
                }
                return;
            }
            const firstRow = firstPressableRow(popup);
            if (highlightedRow !== null || !isSearching || !firstRow) {
                return;
            }
            // Like Base UI's Enter on a highlighted row: the press is the
            // whole action, so cell wrappers don't also finish on Enter.
            event.preventDefault();
            event.stopPropagation();
            preventBaseUIHandler(event);
            firstRow.click();
        },
        [isCellEditor, level, searchText],
    );
};
